import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { LocalizedProcessingBadge, useT, type Translations } from '@/shared/i18n'
import { ToolHero } from '@/shared/ui/ToolHero'

type OutputMime = 'image/jpeg' | 'image/png' | 'image/webp'
type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se'
type SizePresetKey = '300x400' | '600x800' | 'custom'
type PhotoErrors = Translations['photo3x4']['errorsText']

interface CropRect {
  x: number
  y: number
  width: number
  height: number
}

interface Point {
  x: number
  y: number
}

interface LoadedImage {
  file: File
  url: string
  width: number
  height: number
  mime: OutputMime
}

interface PhotoResult {
  blob: Blob
  url: string
  width: number
  height: number
  mime: OutputMime
  generation: number
}

interface DragState {
  mode: 'draw' | 'move' | `resize-${ResizeHandle}`
  startPointer: Point
  startCrop: CropRect
  pointerId: number
}

const UTILITY_KEY = 'photo-3x4'
const TARGET_RATIO = 3 / 4
const MAX_FILE_SIZE = 50 * 1024 * 1024
const MAX_PIXELS = 36_000_000
const MAX_OUTPUT_PIXELS = 36_000_000
const MAX_OUTPUT_WIDTH = 6000
const MIN_OUTPUT_WIDTH = 30
const MIN_CROP_SIZE = 0.04
const ALLOWED_TYPES: OutputMime[] = ['image/jpeg', 'image/png', 'image/webp']

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function clampDimension(value: number): number {
  if (!Number.isFinite(value)) {
    return MIN_OUTPUT_WIDTH
  }
  return clamp(Math.round(value), MIN_OUTPUT_WIDTH, MAX_OUTPUT_WIDTH)
}

function heightForWidth(width: number): number {
  return Math.round(width / TARGET_RATIO)
}

function widthForHeight(height: number): number {
  return Math.round(height * TARGET_RATIO)
}

function fileMime(file: File): OutputMime | null {
  const mime = file.type.toLowerCase()
  if (ALLOWED_TYPES.includes(mime as OutputMime)) {
    return mime as OutputMime
  }

  const name = file.name.toLowerCase()
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg'
  if (name.endsWith('.png')) return 'image/png'
  if (name.endsWith('.webp')) return 'image/webp'
  return null
}

function normalizeRect(rect: CropRect): CropRect {
  const x = rect.width < 0 ? rect.x + rect.width : rect.x
  const y = rect.height < 0 ? rect.y + rect.height : rect.y
  return {
    x: clamp(x, 0, 1),
    y: clamp(y, 0, 1),
    width: Math.abs(rect.width),
    height: Math.abs(rect.height),
  }
}

function clampRect(rect: CropRect): CropRect {
  const normalized = normalizeRect(rect)
  const width = clamp(normalized.width, MIN_CROP_SIZE, 1)
  const height = clamp(normalized.height, MIN_CROP_SIZE, 1)
  return {
    x: clamp(normalized.x, 0, 1 - width),
    y: clamp(normalized.y, 0, 1 - height),
    width,
    height,
  }
}

function createInitialCrop(image: LoadedImage): CropRect {
  const imageRatio = image.width / image.height
  const normalizedRatio = TARGET_RATIO / imageRatio
  let width = 0.78
  let height = width / normalizedRatio

  if (height > 0.78) {
    height = 0.78
    width = height * normalizedRatio
  }

  return {
    x: (1 - width) / 2,
    y: (1 - height) / 2,
    width,
    height,
  }
}

function cropEquals(left: CropRect, right: CropRect): boolean {
  return (
    left.x === right.x &&
    left.y === right.y &&
    left.width === right.width &&
    left.height === right.height
  )
}

function pointerToUnit(event: React.PointerEvent<HTMLElement>, element: HTMLElement): Point {
  const bounds = element.getBoundingClientRect()
  return {
    x: clamp((event.clientX - bounds.left) / bounds.width, 0, 1),
    y: clamp((event.clientY - bounds.top) / bounds.height, 0, 1),
  }
}

function rectFromPoints(start: Point, current: Point, imageRatio: number): CropRect {
  const normalizedRatio = TARGET_RATIO / imageRatio
  const dx = current.x - start.x
  const dy = current.y - start.y
  const signX = dx < 0 ? -1 : 1
  const signY = dy < 0 ? -1 : 1
  let width = Math.max(Math.abs(dx), MIN_CROP_SIZE)
  let height = width / normalizedRatio

  if (height > Math.abs(dy) && Math.abs(dy) >= MIN_CROP_SIZE) {
    height = Math.abs(dy)
    width = height * normalizedRatio
  }

  return clampRect({
    x: start.x,
    y: start.y,
    width: width * signX,
    height: height * signY,
  })
}

function resizeCrop(
  crop: CropRect,
  handle: ResizeHandle,
  pointer: Point,
  imageRatio: number,
): CropRect {
  const anchor: Point = {
    x: handle.includes('w') ? crop.x + crop.width : crop.x,
    y: handle.includes('n') ? crop.y + crop.height : crop.y,
  }

  return rectFromPoints(anchor, pointer, imageRatio)
}

function loadImage(src: string, errors: PhotoErrors): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(errors.corrupt))
    img.src = src
  })
}

function blobFromCanvas(
  canvas: HTMLCanvasElement,
  mime: OutputMime,
  errors: PhotoErrors,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error(errors.exportFailed))
          return
        }
        if (mime === 'image/webp' && blob.type !== 'image/webp') {
          reject(new Error(errors.unsupportedOutput))
          return
        }
        resolve(blob)
      },
      mime,
      mime === 'image/png' ? undefined : 0.95,
    )
  })
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function outputFilename(file: File, mime: OutputMime): string {
  const base = file.name.replace(/\.[^.]+$/, '') || 'photo'
  const ext = mime === 'image/jpeg' ? 'jpg' : mime.replace('image/', '')
  return `${base}-3x4.${ext}`
}

function sizeDelta(original: number, result: number): number {
  if (original <= 0) return 0
  return Math.round(((result - original) / original) * 100)
}

export function Photo3x4Page() {
  const t = useT()
  const [image, setImage] = useState<LoadedImage | null>(null)
  const [crop, setCrop] = useState<CropRect>({ x: 0.2, y: 0.1, width: 0.6, height: 0.8 })
  const [preset, setPreset] = useState<SizePresetKey>('300x400')
  const [targetWidth, setTargetWidth] = useState(300)
  const [targetHeight, setTargetHeight] = useState(400)
  const [backgroundColor, setBackgroundColor] = useState('#ffffff')
  const [result, setResult] = useState<PhotoResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [retryToken, setRetryToken] = useState(0)
  const [dropActive, setDropActive] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const dragRef = useRef<DragState | null>(null)
  const generationRef = useRef(0)
  const cropRef = useRef(crop)

  const clearResult = useCallback(() => {
    setResult((previous) => {
      if (previous) {
        URL.revokeObjectURL(previous.url)
      }
      return null
    })
  }, [])

  const invalidateResult = useCallback((): number => {
    generationRef.current += 1
    clearResult()
    return generationRef.current
  }, [clearResult])

  useEffect(() => {
    return () => {
      generationRef.current += 1
    }
  }, [])

  useEffect(() => {
    return () => {
      if (image) {
        URL.revokeObjectURL(image.url)
      }
    }
  }, [image])

  useEffect(() => {
    return () => {
      if (result) {
        URL.revokeObjectURL(result.url)
      }
    }
  }, [result])

  const imageRatio = image ? image.width / image.height : 1
  const cropPixels = useMemo(() => {
    if (!image) return null
    return {
      width: Math.round(crop.width * image.width),
      height: Math.round(crop.height * image.height),
    }
  }, [crop.height, crop.width, image])
  const outputTooLarge = targetWidth * targetHeight > MAX_OUTPUT_PIXELS
  const canDownload =
    image !== null &&
    result !== null &&
    result.generation === generationRef.current &&
    !isProcessing &&
    !outputTooLarge
  const canRetry =
    image !== null && error !== null && !isProcessing && !outputTooLarge
  const resultDelta = result ? sizeDelta(image?.file.size ?? 0, result.blob.size) : null

  const applySize = useCallback(
    (width: number, nextPreset: SizePresetKey) => {
      const nextWidth = clampDimension(width)
      const nextHeight = heightForWidth(nextWidth)
      if (nextWidth === targetWidth && nextHeight === targetHeight) {
        if (nextPreset !== preset) {
          setPreset(nextPreset)
        }
        return
      }
      setTargetWidth(nextWidth)
      setTargetHeight(nextHeight)
      setPreset(nextPreset)
      setError(nextWidth * nextHeight > MAX_OUTPUT_PIXELS ? t.photo3x4.errorsText.tooLargeOutput : null)
      invalidateResult()
    },
    [
      invalidateResult,
      preset,
      t.photo3x4.errorsText.tooLargeOutput,
      targetHeight,
      targetWidth,
    ],
  )

  const applyCrop = useCallback(
    (nextCrop: CropRect) => {
      if (cropEquals(cropRef.current, nextCrop)) {
        return
      }
      cropRef.current = nextCrop
      setCrop(nextCrop)
      invalidateResult()
    },
    [invalidateResult],
  )

  const setLoadedImage = useCallback(
    async (file: File) => {
      setError(null)
      const requestGeneration = invalidateResult()

      const mime = fileMime(file)
      if (!mime) {
        setError(t.photo3x4.errorsText.unsupported)
        toolError(UTILITY_KEY, 'unsupported_type')
        return
      }
      if (file.size > MAX_FILE_SIZE) {
        setError(t.photo3x4.errorsText.tooLarge)
        toolError(UTILITY_KEY, 'too_large')
        return
      }

      const url = URL.createObjectURL(file)
      try {
        const decoded = await loadImage(url, t.photo3x4.errorsText)
        if (requestGeneration !== generationRef.current) {
          URL.revokeObjectURL(url)
          return
        }
        const width = decoded.naturalWidth
        const height = decoded.naturalHeight
        if (width < 1 || height < 1 || width * height > MAX_PIXELS) {
          throw new Error(t.photo3x4.errorsText.tooLargeImage)
        }

        const nextImage: LoadedImage = { file, url, width, height, mime }
        setImage((previous) => {
          if (previous) {
            URL.revokeObjectURL(previous.url)
          }
          return nextImage
        })
        const initialCrop = createInitialCrop(nextImage)
        cropRef.current = initialCrop
        setCrop(initialCrop)
        setPreset('300x400')
        setTargetWidth(300)
        setTargetHeight(400)
        setBackgroundColor('#ffffff')
      } catch (err) {
        URL.revokeObjectURL(url)
        if (requestGeneration !== generationRef.current) {
          return
        }
        const message = err instanceof Error ? err.message : t.photo3x4.errorsText.corrupt
        setError(message)
        toolError(
          UTILITY_KEY,
          message === t.photo3x4.errorsText.tooLargeImage ? 'too_large_image' : 'corrupt_image',
        )
      }
    },
    [invalidateResult, t.photo3x4.errorsText],
  )

  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      const [file] = Array.from(incoming)
      if (file) {
        void setLoadedImage(file)
      }
    },
    [setLoadedImage],
  )

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        addFiles(event.target.files)
        event.target.value = ''
      }
    },
    [addFiles],
  )

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDropActive(true)
  }, [])

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDropActive(false)
  }, [])

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      event.stopPropagation()
      setDropActive(false)
      if (event.dataTransfer.files.length > 0) {
        addFiles(event.dataTransfer.files)
      }
    },
    [addFiles],
  )

  const handleStagePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!image) return
      const pointer = pointerToUnit(event, event.currentTarget)
      const nextCrop = rectFromPoints(
        pointer,
        { x: pointer.x + MIN_CROP_SIZE, y: pointer.y + MIN_CROP_SIZE },
        imageRatio,
      )
      applyCrop(nextCrop)
      dragRef.current = {
        mode: 'draw',
        startPointer: pointer,
        startCrop: nextCrop,
        pointerId: event.pointerId,
      }
      event.currentTarget.setPointerCapture(event.pointerId)
    },
    [applyCrop, image],
  )

  const handleCropPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.stopPropagation()
      dragRef.current = {
        mode: 'move',
        startPointer: pointerToUnit(event, event.currentTarget.parentElement!),
        startCrop: crop,
        pointerId: event.pointerId,
      }
      event.currentTarget.parentElement?.setPointerCapture(event.pointerId)
    },
    [crop],
  )

  const handleHandlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>, handle: ResizeHandle) => {
      event.stopPropagation()
      const stage = event.currentTarget.closest<HTMLElement>('[data-crop-stage]')
      if (!stage) return
      dragRef.current = {
        mode: `resize-${handle}`,
        startPointer: pointerToUnit(event, stage),
        startCrop: crop,
        pointerId: event.pointerId,
      }
      stage.setPointerCapture(event.pointerId)
    },
    [crop],
  )

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current
      if (!drag || !image || drag.pointerId !== event.pointerId) return

      const pointer = pointerToUnit(event, event.currentTarget)
      if (drag.mode === 'draw') {
        applyCrop(rectFromPoints(drag.startPointer, pointer, imageRatio))
        return
      }

      if (drag.mode === 'move') {
        applyCrop({
          ...drag.startCrop,
          x: clamp(drag.startCrop.x + pointer.x - drag.startPointer.x, 0, 1 - drag.startCrop.width),
          y: clamp(drag.startCrop.y + pointer.y - drag.startPointer.y, 0, 1 - drag.startCrop.height),
        })
        return
      }

      const handle = drag.mode.replace('resize-', '') as ResizeHandle
      applyCrop(resizeCrop(drag.startCrop, handle, pointer, imageRatio))
    },
    [applyCrop, image, imageRatio],
  )

  const stopPointerDrag = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId === event.pointerId) {
      dragRef.current = null
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }, [])

  const handleWidthChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      applySize(Number(event.target.value), 'custom')
    },
    [applySize],
  )

  const handleHeightChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const height = clamp(Math.round(Number(event.target.value)), heightForWidth(MIN_OUTPUT_WIDTH), heightForWidth(MAX_OUTPUT_WIDTH))
      applySize(widthForHeight(height), 'custom')
    },
    [applySize],
  )

  const handleBackgroundChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextBackground = event.target.value
      if (nextBackground === backgroundColor) {
        return
      }
      setBackgroundColor(nextBackground)
      setError(null)
      invalidateResult()
    },
    [backgroundColor, invalidateResult],
  )

  const exportToBlob = useCallback(async (): Promise<Blob> => {
    if (!image) {
      throw new Error(t.photo3x4.errorsText.noImage)
    }
    if (targetWidth * targetHeight > MAX_OUTPUT_PIXELS) {
      throw new Error(t.photo3x4.errorsText.tooLargeOutput)
    }

    const decoded = await loadImage(image.url, t.photo3x4.errorsText)
    const sourceX = Math.round(crop.x * image.width)
    const sourceY = Math.round(crop.y * image.height)
    const sourceWidth = Math.max(1, Math.round(crop.width * image.width))
    const sourceHeight = Math.max(1, Math.round(crop.height * image.height))
    const canvas = document.createElement('canvas')
    canvas.width = targetWidth
    canvas.height = targetHeight

    const context = canvas.getContext('2d')
    if (!context) {
      throw new Error(t.photo3x4.errorsText.canvas)
    }

    context.fillStyle = backgroundColor
    context.fillRect(0, 0, canvas.width, canvas.height)
    context.imageSmoothingEnabled = true
    context.imageSmoothingQuality = 'high'
    context.drawImage(
      decoded,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      targetWidth,
      targetHeight,
    )

    return blobFromCanvas(canvas, image.mime, t.photo3x4.errorsText)
  }, [
    backgroundColor,
    crop.height,
    crop.width,
    crop.x,
    crop.y,
    image,
    t.photo3x4.errorsText,
    targetHeight,
    targetWidth,
  ])

  const handleRetry = useCallback(() => {
    setError(null)
    invalidateResult()
    setRetryToken((current) => current + 1)
  }, [invalidateResult])

  useEffect(() => {
    if (!image || outputTooLarge) {
      clearResult()
      setIsProcessing(false)
      return
    }

    let cancelled = false
    const generation = generationRef.current
    setIsProcessing(true)
    setError(null)
    const timer = window.setTimeout(() => {
      void exportToBlob()
        .then((blob) => {
          const url = URL.createObjectURL(blob)
          if (cancelled || generation !== generationRef.current) {
            URL.revokeObjectURL(url)
            return
          }
          setResult((previous) => {
            if (previous) {
              URL.revokeObjectURL(previous.url)
            }
            return {
              blob,
              url,
              width: targetWidth,
              height: targetHeight,
              mime: image.mime,
              generation,
            }
          })
        })
        .catch((err) => {
          if (!cancelled && generation === generationRef.current) {
            setError(err instanceof Error ? err.message : t.photo3x4.errorsText.exportFailed)
          }
        })
        .finally(() => {
          if (!cancelled && generation === generationRef.current) {
            setIsProcessing(false)
          }
        })
    }, 280)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [
    clearResult,
    exportToBlob,
    image,
    outputTooLarge,
    t.photo3x4.errorsText.exportFailed,
    targetHeight,
    targetWidth,
    retryToken,
  ])

  const handleDownload = useCallback(() => {
    if (
      !image ||
      !result ||
      isProcessing ||
      result.generation !== generationRef.current
    ) {
      return
    }
    const startTime = performance.now()
    toolInvoke(UTILITY_KEY, {
      preset,
      width: targetWidth,
      height: targetHeight,
      mime: image.mime,
    })
    downloadBlob(result.blob, outputFilename(image.file, result.mime))
    toolSuccess(UTILITY_KEY, Math.round(performance.now() - startTime), {
      preset,
      width: result.width,
      height: result.height,
      mime: result.mime,
    })
  }, [
    image,
    isProcessing,
    preset,
    result,
    targetHeight,
    targetWidth,
  ])

  return (
    <div className="mx-auto max-w-6xl">
      <ToolHero
        eyebrow={t.photo3x4.page.eyebrow}
        title={t.photo3x4.page.title}
        description={t.photo3x4.page.description}
        chips={[t.photo3x4.formats, t.common.maxFileSize]}
        collapsed={Boolean(image)}
      />

      <div className={cn('grid grid-cols-1 gap-6', image && 'lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start')}>
        <div className="min-w-0 space-y-6">
          {!image && (
            <Card
              className={cn('mx-auto max-w-3xl transition-colors', dropActive && 'border-primary bg-primary/5')}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <CardContent className="p-4 text-center sm:p-8">
                <div className="mx-auto mb-5 hidden h-16 w-16 place-items-center sm:grid rounded-md border bg-background text-sm font-semibold text-muted-foreground">
                  3x4
                </div>
                <p className="mb-2 hidden text-lg font-medium [@media(pointer:fine)]:block">{t.photo3x4.dropTitle}</p>
                <p className="mx-auto mb-5 max-w-md text-sm leading-6 text-muted-foreground">
                  {t.photo3x4.dropDescription}
                </p>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp"
                  className="hidden"
                  onChange={handleInputChange}
                />
                <Button onClick={() => inputRef.current?.click()}>
                  {t.photo3x4.chooseFile}
                </Button>
              </CardContent>
            </Card>
          )}

          {image && (
            <>
              <Card className="overflow-hidden">
                <CardContent className="space-y-4 p-4 sm:p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <h2 className="truncate text-lg font-semibold tracking-normal" title={image.file.name}>
                        {image.file.name}
                      </h2>
                      <div className="mt-2 hidden flex-wrap gap-2 sm:flex">
                        <span className="rounded-md border bg-background px-2 py-1 text-xs font-medium text-muted-foreground">
                          {image.mime.replace('image/', '').toUpperCase()}
                        </span>
                        <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                          {formatSize(image.file.size)}
                        </span>
                        <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                          {image.width} x {image.height}
                        </span>
                      </div>
                    </div>
                    <Button variant="outline" onClick={() => inputRef.current?.click()}>
                      {t.photo3x4.replaceImage}
                    </Button>
                  </div>

                  <input
                    ref={inputRef}
                    type="file"
                    accept=".png,.jpg,.jpeg,.webp"
                    className="hidden"
                    onChange={handleInputChange}
                  />

                  <div
                    data-crop-stage
                    className="relative mx-auto w-full max-w-4xl touch-none select-none overflow-hidden rounded-md border bg-muted"
                    style={{ aspectRatio: `${image.width} / ${image.height}` }}
                    onPointerDown={handleStagePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={stopPointerDrag}
                    onPointerCancel={stopPointerDrag}
                  >
                    <img
                      src={image.url}
                      alt={image.file.name}
                      className="h-full w-full object-fill"
                      draggable={false}
                    />
                    <div
                      className="absolute cursor-move border-2 border-white shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]"
                      style={{
                        left: `${crop.x * 100}%`,
                        top: `${crop.y * 100}%`,
                        width: `${crop.width * 100}%`,
                        height: `${crop.height * 100}%`,
                      }}
                      onPointerDown={handleCropPointerDown}
                    >
                      <div className="grid h-full w-full grid-cols-3 grid-rows-3">
                        {Array.from({ length: 9 }).map((_, index) => (
                          <span key={index} className="border border-white/35" aria-hidden />
                        ))}
                      </div>
                      {(['nw', 'ne', 'sw', 'se'] as ResizeHandle[]).map((handle) => (
                        <button
                          key={handle}
                          type="button"
                          aria-label={t.photo3x4.resizeHandle}
                          className={cn(
                            'absolute h-5 w-5 rounded-full border-2 border-white bg-primary shadow-sm',
                            handle === 'nw' && '-left-2.5 -top-2.5 cursor-nwse-resize',
                            handle === 'ne' && '-right-2.5 -top-2.5 cursor-nesw-resize',
                            handle === 'sw' && '-bottom-2.5 -left-2.5 cursor-nesw-resize',
                            handle === 'se' && '-bottom-2.5 -right-2.5 cursor-nwse-resize',
                          )}
                          onPointerDown={(event) => handleHandlePointerDown(event, handle)}
                        />
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="hidden md:block">
                <CardContent className="space-y-4 p-4 sm:p-5">
                  <h2 className="text-lg font-semibold tracking-normal">{t.photo3x4.previewTitle}</h2>
                  <div className="grid grid-cols-2 gap-2 sm:gap-4">
                    <PreviewPanel
                      label={t.photo3x4.sourceLabel}
                      src={image.url}
                      name={image.file.name}
                      size={formatSize(image.file.size)}
                      dimensions={`${cropPixels?.width ?? 0} x ${cropPixels?.height ?? 0}`}
                    />
                    <PreviewPanel
                      label={t.photo3x4.resultLabel}
                      src={result?.url ?? image.url}
                      name={result ? outputFilename(image.file, result.mime) : t.photo3x4.waitingPreview}
                      size={
                        result
                          ? formatSize(result.blob.size)
                          : isProcessing
                            ? t.photo3x4.previewUpdating
                            : t.photo3x4.notReady
                      }
                      dimensions={`${targetWidth} x ${targetHeight}`}
                      delta={resultDelta}
                      muted={!result}
                      frame="portrait"
                    />
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {error && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}
        </div>

        {image && (
          <aside className="min-w-0 space-y-4 lg:sticky lg:top-24">
            <Card>
              <CardContent className="space-y-5 p-5">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold tracking-normal">{t.photo3x4.settings}</h2>
                  <LocalizedProcessingBadge variant="local" />
                </div>

                <div className="space-y-2">
                  <div className="text-sm font-medium">{t.photo3x4.outputSize}</div>
                  <div className="grid grid-cols-3 overflow-hidden rounded-md border">
                    {t.photo3x4.presets.map((item) => (
                      <button
                        key={item.key}
                        type="button"
                        className={cn(
                          'px-3 py-2 text-sm font-medium transition-colors',
                          item.key !== '300x400' && 'border-l',
                          preset === item.key
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-background hover:bg-muted',
                        )}
                        onClick={() => {
                          const presetKey = item.key as SizePresetKey
                          if (presetKey === 'custom') {
                            setPreset('custom')
                            return
                          }
                          applySize(presetKey === '300x400' ? 300 : 600, presetKey)
                        }}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <label className="space-y-2">
                    <span className="text-sm font-medium">{t.photo3x4.width}</span>
                    <input
                      type="number"
                      min={MIN_OUTPUT_WIDTH}
                      max={MAX_OUTPUT_WIDTH}
                      step={1}
                      value={targetWidth}
                      onChange={handleWidthChange}
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </label>
                  <label className="space-y-2">
                    <span className="text-sm font-medium">{t.photo3x4.height}</span>
                    <input
                      type="number"
                      min={heightForWidth(MIN_OUTPUT_WIDTH)}
                      max={heightForWidth(MAX_OUTPUT_WIDTH)}
                      step={1}
                      value={targetHeight}
                      onChange={handleHeightChange}
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </label>
                </div>

                <div className="rounded-md border bg-background px-3 py-2 text-sm text-muted-foreground">
                  {t.photo3x4.aspectLock}
                </div>

                <label className="flex items-center justify-between gap-3 rounded-md border bg-background px-3 py-2">
                  <span className="text-sm font-medium">{t.photo3x4.backgroundColor}</span>
                  <input
                    type="color"
                    value={backgroundColor}
                    onChange={handleBackgroundChange}
                    className="h-9 w-12 rounded border bg-background p-1"
                  />
                </label>

                <div className="grid grid-cols-2 gap-2 text-center">
                  <div className="rounded-md bg-muted p-3">
                    <div className="font-semibold tabular-nums">
                      {cropPixels?.width ?? 0} x {cropPixels?.height ?? 0}
                    </div>
                    <div className="text-xs text-muted-foreground">{t.photo3x4.cropSize}</div>
                  </div>
                  <div className="rounded-md bg-muted p-3">
                    <div className="font-semibold tabular-nums">
                      {targetWidth} x {targetHeight}
                    </div>
                    <div className="text-xs text-muted-foreground">{t.photo3x4.targetSize}</div>
                  </div>
                </div>

                <Button
                  className="w-full"
                  size="lg"
                  onClick={canRetry ? handleRetry : handleDownload}
                  disabled={!canDownload && !canRetry}
                >
                  {isProcessing
                    ? t.photo3x4.processing
                    : canRetry
                      ? t.photo3x4.retry
                      : t.photo3x4.download}
                </Button>
              </CardContent>
            </Card>

            <p className="rounded-md border bg-card px-3 py-2 text-xs leading-5 text-muted-foreground">
              {t.photo3x4.noComplianceNote}
            </p>
            <p className="rounded-md border bg-card px-3 py-2 text-xs leading-5 text-muted-foreground">
              {t.photo3x4.privacyNote}
            </p>
          </aside>
        )}
      </div>

      <section className="mt-12 grid gap-4 border-t pt-8 lg:grid-cols-3">
        {t.photo3x4.page.faq.map((item) => (
          <div key={item.question}>
            <h2 className="text-base font-semibold tracking-normal">{item.question}</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              {item.answer}
            </p>
          </div>
        ))}
      </section>
    </div>
  )
}

function PreviewPanel({
  label,
  src,
  name,
  size,
  dimensions,
  delta,
  muted = false,
  frame = 'landscape',
}: {
  label: string
  src: string
  name: string
  size: string
  dimensions: string
  delta?: number | null
  muted?: boolean
  frame?: 'landscape' | 'portrait'
}) {
  return (
    <div className="overflow-hidden rounded-md border bg-background">
      <div className={cn('grid bg-muted p-3', muted && 'opacity-55')}>
        <div
          className={cn(
            'mx-auto w-full overflow-hidden rounded-md bg-background',
            frame === 'portrait' ? 'max-w-48' : 'max-w-full',
          )}
          style={{ aspectRatio: frame === 'portrait' ? '3 / 4' : '16 / 9' }}
        >
          <img src={src} alt={name} className="h-full w-full object-contain" />
        </div>
      </div>
      <div className="space-y-1 p-2 sm:space-y-3 sm:p-3">
        <div>
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="truncate text-sm font-medium" title={name}>
            {name}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="rounded-md border bg-card px-2 py-1 text-xs text-muted-foreground">
            {dimensions}
          </span>
          <span className="rounded-md border bg-card px-2 py-1 text-xs text-muted-foreground">
            {size}
            {typeof delta === 'number' && (
              <span
                className={cn(
                  'ml-1 font-medium',
                  delta <= 0 ? 'text-primary' : 'text-muted-foreground',
                )}
              >
                {delta > 0 ? '+' : ''}
                {delta}%
              </span>
            )}
          </span>
        </div>
      </div>
    </div>
  )
}
